import React, { useState, useMemo, useContext, useEffect } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  KeyboardAvoidingView,
  Modal,
  ActivityIndicator,
  TextInput,
  Platform,
  Dimensions,
  PixelRatio,
  StyleSheet,
} from "react-native";
import { MaterialCommunityIcons, Ionicons } from "@expo/vector-icons";
import { useNavigation, useRoute } from "@react-navigation/native";
import Colours from "../../utils/Colours";
import AppContext from "../../context/AppContext";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import axios from "../../api/axios";
import TopHeaderComponent from "../aTopHeader/TopHeaderComponent";
import ErrorAlertComp from "../../alertComp/ErrorAlertComp";
import { SafeAreaView } from "react-native-safe-area-context";

// Adjust path imports as required by your project structure
// import TopHeaderComponent from "../components/TopHeaderComponent";
// import ErrorAlertComp from "../components/ErrorAlertComp";
// import { AppContext } from "../context/AppContext";
// import useAxiosPrivate from "../hooks/useAxiosPrivate";
// import axios from "axios";
// import Colours from "../constants/Colours";

const { width, height } = Dimensions.get("window");
const BASE_WIDTH = 375;
const BASE_HEIGHT = 812;

const dynamicFontSize = (size) => {
  const scaleFactor = Math.min(width / BASE_WIDTH, height / BASE_HEIGHT);
  return Math.round(PixelRatio.roundToNearestPixel(size * scaleFactor));
};
const dynamicSpacing = (size) => (width / BASE_WIDTH) * size;
const dynamicSize = (size) => (width / BASE_WIDTH) * size;

const ReviewAndPay = () => {
  const { setAuth } = useContext(AppContext);

  const [isErrorCompVisible, setIsErrorCompVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [payTotalBill, setPayTotalBill] = useState(true);
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [isCustomSelected, setIsCustomSelected] = useState(false);

  // Tracks expanding/collapsing state for individual order batches
  const [expandedBatches, setExpandedBatches] = useState({});

  // Local editable state for order batches
  const [orderBatches, setOrderBatches] = useState([]);

  // Paystack Native Confirmation Alert States
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);

  const navigation = useNavigation();
  const route = useRoute();
  const { userToken } = useContext(AppContext);
  const axiosPrivate = useAxiosPrivate();
  const isLoggedIn = !!userToken;

  // Retrieve incoming transaction parameters
  const selectedTable = route?.params?.selectedTable;
  const tableId = selectedTable?._id;
  const tableName = selectedTable?.tableName || "Table Summary";

  console.log("selectedTabledd", selectedTable);

  // Initialize order batches state from selectedTable orders prop
  useEffect(() => {
    if (selectedTable?.orders && Array.isArray(selectedTable.orders)) {
      const parsedBatches = selectedTable.orders.map((orderBatch, bIndex) => {
        // Evaluate batch status (defaults to PENDING if status field is missing)
        const rawStatus = (
          orderBatch.status || (orderBatch.isPaid ? "PAID" : "PENDING")
        ).toUpperCase();

        return {
          batchId: orderBatch._id || orderBatch.orderToken || `batch-${bIndex}`,
          orderToken: orderBatch.orderToken,
          status: rawStatus,
          items: (orderBatch.items || []).map((item, iIndex) => {
            const unitPrice =
              item.price || (item.quantity ? item.totalItemPrice / item.quantity : 0);
            return {
              id: item._id || `${orderBatch.orderToken || bIndex}-${item.menuItemId || iIndex}`,
              menuItemId: item.menuItemId,
              menuType: item.menuType,
              name: item.name,
              qty: item.quantity,
              initialQty: item.quantity, // Enforces upper limit when restoring quantities
              unitPrice: unitPrice,
            };
          }),
        };
      });

      setOrderBatches(parsedBatches);

      // Auto-expand pending batches by default
      const initialExpandedState = {};
      parsedBatches.forEach((b) => {
        if (b.status === "PENDING" || b.status === "UNPAID") {
          initialExpandedState[b.batchId] = true;
        }
      });
      setExpandedBatches(initialExpandedState);
    }
  }, [selectedTable]);

  // // Recalculate total bill across all order batches dynamically
  // const orderTotalBill = useMemo(() => {
  //   return orderBatches.reduce((batchSum, batch) => {
  //     const batchItemsSum = batch.items.reduce(
  //       (itemSum, item) => itemSum + item.unitPrice * item.qty,
  //       0,
  //     );
  //     return batchSum + batchItemsSum;
  //   }, 0);
  // }, [orderBatches]);
  // Recalculate total bill strictly for unpaid or pending order batches
  const orderTotalBill = useMemo(() => {
    return orderBatches.reduce((batchSum, batch) => {
      // Skip already paid batches entirely from the total calculation
      if (batch.status !== "PENDING" && batch.status !== "UNPAID") {
        return batchSum;
      }

      const batchItemsSum = batch.items.reduce(
        (itemSum, item) => itemSum + item.unitPrice * item.qty,
        0,
      );
      return batchSum + batchItemsSum;
    }, 0);
  }, [orderBatches]);

  const hasUnpaidBatches = orderBatches.some(
    (batch) => batch.status === "PENDING" || batch.status === "UNPAID",
  );

  // Dynamically extract final target sum depending on user input choice
  const finalCalculatedSum = useMemo(() => {
    if (payTotalBill) return orderTotalBill;
    return Number(customAmount.replace(/[^0-9]/g, "")) || 0;
  }, [payTotalBill, orderTotalBill, customAmount]);

  const canProceed = useMemo(() => {
    const isAmountValid = finalCalculatedSum > 0;
    return isLoggedIn ? isAmountValid : isAmountValid && whatsappNumber?.length > 10;
  }, [finalCalculatedSum, isLoggedIn, whatsappNumber]);

  // Toggle batch accordion expand view
  const toggleBatchExpand = (batchId) => {
    setExpandedBatches((prev) => ({
      ...prev,
      [batchId]: !prev[batchId],
    }));
  };

  // Handles quantity adjustments strictly for unpaid/pending batches
  const handlePendingItemQuantityChange = (batchId, itemId, action) => {
    setOrderBatches((prevBatches) =>
      prevBatches.map((batch) => {
        // Prevent editing paid batches
        if (
          batch.batchId !== batchId ||
          (batch.status !== "PENDING" && batch.status !== "UNPAID")
        ) {
          return batch;
        }

        const updatedItems = batch.items.map((item) => {
          if (item.id === itemId) {
            const maxAllowed = item.initialQty;
            let newQty = item.qty;

            if (action === "add" && newQty < maxAllowed) {
              newQty += 1;
            } else if (action === "subtract" && newQty > 0) {
              newQty -= 1;
            }

            return { ...item, qty: newQty };
          }
          return item;
        });

        return { ...batch, items: updatedItems };
      }),
    );
  };

  const handleProceedClick = () => {
    if (!canProceed) return;
    setIsConfirmModalVisible(true);
  };

  console.log("orderBatches", orderBatches);

  const executeOrdersAndPayments = async (paymentTypeInput = "now") => {
    // Coerce paymentType to a string to prevent React event object bugs
    const paymentType = typeof paymentTypeInput === "string" ? paymentTypeInput : "now";

    if (isLoading) return false;

    if (!tableId || orderBatches.length === 0) {
      setErrorMessage("Table context or order batches are missing.");
      setIsErrorCompVisible(true);
      return false;
    }

    try {
      setIsLoading(true);
      setIsConfirmModalVisible(false);

      let finalPhoneNumber = whatsappNumber;
      if (!isLoggedIn) {
        let cleaned = whatsappNumber.replace(/\D/g, "").replace(/^0+/, "");
        if (cleaned.startsWith("234")) cleaned = cleaned.slice(3);
        cleaned = cleaned.replace(/^0+/, "");

        if (cleaned.length !== 10) {
          setErrorMessage("You might have entered a wrong number");
          setIsErrorCompVisible(true);
          setIsLoading(false);
          return;
        }
        finalPhoneNumber = `234${cleaned}`;
      }

      // Filter to include only unpaid or pending batches
      const unpaidBatches = orderBatches.filter(
        (batch) => batch.status === "PENDING" || batch.status === "UNPAID",
      );

      console.log("unpaidBatches", unpaidBatches);

      // Map only the unpaid batches into the flat items array format
      const finalOrderItems = unpaidBatches.flatMap((batch) =>
        batch.items
          .filter((item) => item.qty > 0) // Only include items with a quantity greater than 0
          .map((item) => ({
            menuItemId: item.menuItemId,
            name: item.name,
            quantity: item.qty,
            unitPrice: item.unitPrice,
            totalItemPrice: item.unitPrice * item.qty,
            menuType: item.menuType,
          })),
      );

      const isTopUp = route.params?.isTopUp === true || route.params?.isTopUp === "true";
      const tableAnonymity = selectedTable?.isAnonymous || route.params?.tableAnonymity;
      const showId = selectedTable?.showId || route.params?.showId;

      const payload = {
        tableId,
        showId,
        items: finalOrderItems,
        tableAnonymity,
        paymentType,
        isTopUp,
        customSpend: finalCalculatedSum,
        adjustedBatches: unpaidBatches, // Only push unpaid/pending batches
      };

      console.log("Initializing checkout intent:", payload);

      const checkoutSetup = await axiosPrivate.post("/payments2/initialize-checkout", payload);

      if (checkoutSetup.data.status === "success" || checkoutSetup.data.success) {
        const responseData = checkoutSetup.data;
        const targetTableId = responseData.tableData?._id || responseData?.data?.tableId || tableId;

        console.log("responseData", responseData);

        setAuth((prev) => ({
          ...prev,
          isProfileComplete: true,
          hasPendingPrepaidOrder: checkoutSetup.data?.hasPendingPrepaidOrder,
          pendingOrderDetails: checkoutSetup.data?.pendingOrderDetails,
          reference: checkoutSetup.data?.data?.reference,
        }));
        // return true;
      } else {
        setErrorMessage("Gateway initialization rejection.");
        setIsErrorCompVisible(true);
        return false;
      }
    } catch (error) {
      console.error("Order dispatch communication error:", error);
      setErrorMessage(
        error?.response?.data?.message || "Failed to submit order. Please try again.",
      );
      setIsErrorCompVisible(true);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <TopHeaderComponent headerText={tableName} hideScreen={() => navigation.goBack()} />

      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={{ flex: 1 }}
      >
        <ScrollView
          contentContainerStyle={{
            padding: dynamicSpacing(20),
            paddingBottom: dynamicSpacing(200),
            backgroundColor: Colours.LIGHTEST,
          }}
          showsVerticalScrollIndicator={false}
        >
          {/* ── SECTION 1: TOTAL BILL & BATCHES REVIEW ── */}
          <View style={styles.section}>
            <Text style={styles.sectionLabel}>TOTAL BILL TO PAY</Text>
            <TouchableOpacity
              style={[styles.billCard, payTotalBill && styles.billCardActive]}
              onPress={() => {
                setPayTotalBill(true);
                setIsCustomSelected(false);
                setCustomAmount("");
              }}
              activeOpacity={0.85}
            >
              <View style={styles.billCardHeader}>
                <MaterialCommunityIcons
                  name="receipt"
                  size={dynamicSize(22)}
                  color={payTotalBill ? "white" : Colours.PRIMARY}
                />
                <Text style={[styles.billCardTitle, payTotalBill && { color: "white" }]}>
                  Order Digital Total
                </Text>
                {payTotalBill && (
                  <MaterialCommunityIcons
                    name="check-circle"
                    size={dynamicSize(20)}
                    color="white"
                    style={{ marginLeft: "auto" }}
                  />
                )}
              </View>

              <Text style={[styles.billCardAmount, payTotalBill && { color: "white" }]}>
                ₦{orderTotalBill.toLocaleString()}
              </Text>
            </TouchableOpacity>

            {/* ── BATCH LISTING (ORDER-BASED FILTERING) ── */}
            <View style={{ marginTop: dynamicSpacing(16) }}>
              <Text style={styles.sectionLabel}>ORDER BATCHES</Text>
              {orderBatches.map((batch, index) => {
                const isPending = batch.status === "PENDING" || batch.status === "UNPAID";
                const isExpanded = !!expandedBatches[batch.batchId];
                const batchSubtotal = batch.items.reduce((sum, i) => sum + i.unitPrice * i.qty, 0);

                return (
                  <View key={batch.batchId} style={styles.batchCardContainer}>
                    <TouchableOpacity
                      style={styles.batchHeader}
                      onPress={() => toggleBatchExpand(batch.batchId)}
                      activeOpacity={0.8}
                    >
                      <View style={{ flex: 1 }}>
                        <View
                          style={{
                            flexDirection: "row",
                            alignItems: "center",
                            gap: dynamicSpacing(8),
                          }}
                        >
                          <Text style={styles.batchTitle}>Batch #{index + 1}</Text>
                          <View
                            style={[
                              styles.statusBadge,
                              {
                                backgroundColor: isPending ? "#FFF3CD" : "#D4EDDA",
                              },
                            ]}
                          >
                            <Text
                              style={[
                                styles.statusBadgeText,
                                {
                                  color: isPending ? "#856404" : "#155724",
                                },
                              ]}
                            >
                              {isPending ? "Unpaid" : "Paid"}
                            </Text>
                          </View>
                        </View>
                        <Text style={styles.batchSubtotalText}>
                          Subtotal: ₦{batchSubtotal.toLocaleString()}
                        </Text>
                      </View>

                      <MaterialCommunityIcons
                        name={isExpanded ? "chevron-up" : "chevron-down"}
                        size={22}
                        color="#666"
                      />
                    </TouchableOpacity>

                    {/* Batch Items Listing */}
                    {isExpanded && (
                      <View style={styles.batchItemsContainer}>
                        {batch.items.map((item) => {
                          const itemTotal = item.unitPrice * item.qty;

                          return (
                            <View key={item.id} style={styles.reviewItemRow}>
                              <View
                                style={{
                                  flex: 1,
                                  paddingRight: dynamicSpacing(8),
                                }}
                              >
                                <Text
                                  style={[
                                    styles.itemNameText,
                                    item.qty === 0 && styles.itemDisabledText,
                                  ]}
                                  numberOfLines={1}
                                >
                                  {item.name}
                                </Text>
                                <Text style={styles.itemSubPriceText}>
                                  ₦{item.unitPrice.toLocaleString()} each
                                </Text>
                              </View>

                              {/* Active Counters for Pending; Static quantity display for Paid */}
                              {isPending ? (
                                <View style={styles.reviewCounterContainer}>
                                  <TouchableOpacity
                                    onPress={() =>
                                      handlePendingItemQuantityChange(
                                        batch.batchId,
                                        item.id,
                                        "subtract",
                                      )
                                    }
                                    disabled={item.qty === 0}
                                    style={[styles.counterBtn, item.qty === 0 && { opacity: 0.3 }]}
                                  >
                                    <Ionicons
                                      name="remove"
                                      size={dynamicSize(14)}
                                      color={Colours.PRIMARY}
                                    />
                                  </TouchableOpacity>

                                  <Text style={styles.counterText}>{item.qty}</Text>

                                  <TouchableOpacity
                                    onPress={() =>
                                      handlePendingItemQuantityChange(batch.batchId, item.id, "add")
                                    }
                                    disabled={item.qty >= item.initialQty}
                                    style={[
                                      styles.counterBtn,
                                      { backgroundColor: Colours.PRIMARY },
                                      item.qty >= item.initialQty && {
                                        opacity: 0.3,
                                      },
                                    ]}
                                  >
                                    <Ionicons name="add" size={dynamicSize(14)} color="white" />
                                  </TouchableOpacity>
                                </View>
                              ) : (
                                <Text style={styles.paidQtyText}>{item.qty}x</Text>
                              )}

                              <Text
                                style={[
                                  styles.itemPriceText,
                                  item.qty === 0 && styles.itemDisabledText,
                                ]}
                              >
                                ₦{itemTotal.toLocaleString()}
                              </Text>
                            </View>
                          );
                        })}
                      </View>
                    )}
                  </View>
                );
              })}

              {orderBatches.length === 0 && (
                <Text style={styles.noOrdersText}>No order batches found for this table.</Text>
              )}
            </View>
          </View>

          {/* ── SECTION 2: ALTERNATIVE CUSTOM WAITER BILL ENTRY ── */}
          {/* <View style={styles.section}>
            <Text style={styles.sectionLabel}>ENTER AMOUNT TOLD BY WAITER</Text>
            <View style={styles.inputSection}>
              <View style={[styles.premiumInputContainer, isCustomSelected && styles.inputFocused]}>
                <Text style={styles.inputCurrency}>₦</Text>
                <TextInput
                  style={styles.fieldInput}
                  placeholder="Input custom waiter stated amount"
                  placeholderTextColor="#A3A3A3"
                  keyboardType="numeric"
                  value={customAmount}
                  onChangeText={(text) => {
                    const cleaned = text.replace(/[^0-9]/g, "");
                    setCustomAmount(cleaned);
                    setIsCustomSelected(cleaned !== "");

                    if (cleaned !== "") {
                      setPayTotalBill(false);
                    } else {
                      setPayTotalBill(true);
                    }
                  }}
                  onFocus={() => {
                    setIsCustomSelected(true);
                    setPayTotalBill(false);
                  }}
                />
              </View>
            </View>
          </View> */}

          {/* ── FINAL SUBMISSION BUTTON ── */}
          {hasUnpaidBatches && (
            <TouchableOpacity
              style={[
                styles.proceedBtn,
                !canProceed && styles.disabledBtn,
                { marginTop: dynamicSpacing(24) },
              ]}
              onPress={handleProceedClick}
              disabled={!canProceed || isLoading}
            >
              {isLoading ? (
                <ActivityIndicator color="white" />
              ) : (
                <>
                  <Text style={styles.proceedBtnText}>
                    {isLoggedIn ? "Confirm & Pay Stated Sum" : "Verify Number via OTP"}
                  </Text>
                  <MaterialCommunityIcons
                    name={isLoggedIn ? "credit-card-outline" : "arrow-right"}
                    size={dynamicSize(20)}
                    color="white"
                  />
                </>
              )}
            </TouchableOpacity>
          )}

          <Text style={styles.footerNote}>
            Moniepoint instant transaction systems secure and tokenise checkout verification layers.
          </Text>
        </ScrollView>
      </KeyboardAvoidingView>

      {/* ── CONFIRMATION MODAL OVERLAY ── */}
      <Modal visible={isConfirmModalVisible} transparent={true} animationType="fade">
        <View style={styles.alertModalCenteredBackdrop}>
          <View style={styles.alertModalContainerBox}>
            <View style={styles.alertIconHeaderContainer}>
              <MaterialCommunityIcons name="shield-check" size={32} color={Colours.PRIMARY} />
            </View>
            <Text style={styles.alertModalHeading}>Confirm Bill Total</Text>
            <Text style={styles.alertModalMessage}>
              You are about to pay a total sum of {"\n"}
              <Text style={styles.alertModalAmountHighlight}>
                ₦{finalCalculatedSum.toLocaleString()}
              </Text>
            </Text>

            <View style={styles.alertModalButtonWrapper}>
              <TouchableOpacity
                style={styles.alertModalCancelButton}
                onPress={() => setIsConfirmModalVisible(false)}
              >
                <Text style={styles.alertModalCancelText}>Cancel</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.alertModalOkButton}
                onPress={() => executeOrdersAndPayments("now")}
              >
                <Text style={styles.alertModalOkText}>Pay</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* ── ERROR ALERT MODAL ── */}
      <Modal visible={isErrorCompVisible} transparent={true} animationType="fade">
        <ErrorAlertComp message={errorMessage} onClose={() => setIsErrorCompVisible(false)} />
      </Modal>
    </SafeAreaView>
  );
};

export default ReviewAndPay;

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: Colours.LIGHTEST,
  },
  section: {
    marginBottom: dynamicSpacing(20),
  },
  sectionLabel: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(12),
    color: "#888",
    marginBottom: dynamicSpacing(8),
    letterSpacing: 0.5,
  },
  billCard: {
    backgroundColor: "white",
    borderRadius: dynamicSize(16),
    padding: dynamicSpacing(16),
    borderWidth: 1.5,
    borderColor: "#EBEBEF",
  },
  billCardActive: {
    backgroundColor: Colours.PRIMARY,
    borderColor: Colours.PRIMARY,
  },
  billCardHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: dynamicSpacing(8),
  },
  billCardTitle: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(15),
    color: Colours.PRIMARY,
  },
  billCardAmount: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(26),
    color: "#222",
    marginTop: dynamicSpacing(8),
  },

  /* ── BATCH CARD STYLES ── */
  batchCardContainer: {
    backgroundColor: "white",
    borderRadius: dynamicSize(12),
    marginBottom: dynamicSpacing(12),
    borderWidth: 1,
    borderColor: "#EBEBEF",
    overflow: "hidden",
  },
  batchHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: dynamicSpacing(14),
    backgroundColor: "#FAFAFA",
  },
  batchTitle: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(14),
    color: "#222",
  },
  batchSubtotalText: {
    fontFamily: "Sen-Regular",
    fontSize: dynamicFontSize(12),
    color: "#666",
    marginTop: dynamicSpacing(2),
  },
  statusBadge: {
    paddingHorizontal: dynamicSpacing(8),
    paddingVertical: dynamicSpacing(2),
    borderRadius: dynamicSize(6),
  },
  statusBadgeText: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(10),
    textTransform: "uppercase",
  },
  batchItemsContainer: {
    paddingHorizontal: dynamicSpacing(14),
    paddingBottom: dynamicSpacing(8),
    borderTopWidth: 1,
    borderTopColor: "#F0F0F2",
  },
  noOrdersText: {
    fontFamily: "Sen-Regular",
    fontSize: dynamicFontSize(13),
    color: "#888",
    textAlign: "center",
    marginTop: dynamicSpacing(8),
  },

  /* ── BATCH ITEM COUNTER STYLES ── */
  reviewItemRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: dynamicSpacing(10),
    borderBottomWidth: 1,
    borderBottomColor: "#F5F5F7",
  },
  itemNameText: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(13),
    color: "#222",
  },
  itemSubPriceText: {
    fontFamily: "Sen-Regular",
    fontSize: dynamicFontSize(11),
    color: "#777",
    marginTop: dynamicSpacing(2),
  },
  itemDisabledText: {
    textDecorationLine: "line-through",
    opacity: 0.4,
  },
  reviewCounterContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F0F0F2",
    borderRadius: dynamicSize(16),
    padding: dynamicSpacing(3),
    gap: dynamicSpacing(6),
    marginHorizontal: dynamicSpacing(6),
  },
  counterBtn: {
    width: dynamicSize(24),
    height: dynamicSize(24),
    borderRadius: dynamicSize(12),
    backgroundColor: "white",
    justifyContent: "center",
    alignItems: "center",
  },
  counterText: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(13),
    color: "#222",
    minWidth: dynamicSize(16),
    textAlign: "center",
  },
  paidQtyText: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(13),
    color: "#555",
    marginHorizontal: dynamicSpacing(12),
  },
  itemPriceText: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(13),
    color: "#222",
    minWidth: dynamicSize(65),
    textAlign: "right",
  },

  /* ── CUSTOM INPUT STYLES ── */
  inputSection: {
    marginTop: dynamicSpacing(4),
  },
  premiumInputContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "white",
    borderRadius: dynamicSize(12),
    paddingHorizontal: dynamicSpacing(14),
    height: dynamicSize(50),
    borderWidth: 1,
    borderColor: "#EBEBEF",
  },
  inputFocused: {
    borderColor: Colours.PRIMARY,
  },
  inputCurrency: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(16),
    color: "#222",
    marginRight: dynamicSpacing(8),
  },
  fieldInput: {
    flex: 1,
    fontFamily: "Sen-Regular",
    fontSize: dynamicFontSize(14),
    color: "#222",
  },

  /* ── BUTTONS & FOOTER ── */
  proceedBtn: {
    backgroundColor: Colours.PRIMARY,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    height: dynamicSize(52),
    borderRadius: dynamicSize(14),
    gap: dynamicSpacing(8),
  },
  disabledBtn: {
    opacity: 0.5,
  },
  proceedBtnText: {
    color: "white",
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(15),
  },
  footerNote: {
    fontFamily: "Sen-Regular",
    fontSize: dynamicFontSize(11),
    color: "#888",
    textAlign: "center",
    marginTop: dynamicSpacing(12),
  },

  /* ── MODAL STYLES ── */
  alertModalCenteredBackdrop: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    justifyContent: "center",
    alignItems: "center",
    padding: dynamicSpacing(24),
  },
  alertModalContainerBox: {
    width: "100%",
    maxWidth: 320,
    backgroundColor: "white",
    borderRadius: dynamicSize(20),
    padding: dynamicSpacing(24),
    alignItems: "center",
    elevation: 8,
  },
  alertIconHeaderContainer: {
    marginBottom: dynamicSpacing(12),
  },
  alertModalHeading: {
    fontFamily: "Sen-Bold",
    fontSize: dynamicFontSize(18),
    color: "#222",
    marginBottom: dynamicSpacing(8),
  },
  alertModalMessage: {
    fontFamily: "Sen-Regular",
    fontSize: dynamicFontSize(14),
    color: "#666",
    textAlign: "center",
    marginBottom: dynamicSpacing(20),
  },
  alertModalAmountHighlight: {
    fontFamily: "Sen-Bold",
    color: Colours.PRIMARY,
    fontSize: dynamicFontSize(18),
  },
  alertModalButtonWrapper: {
    flexDirection: "row",
    gap: dynamicSpacing(12),
    width: "100%",
  },
  alertModalCancelButton: {
    flex: 1,
    height: dynamicSize(44),
    borderRadius: dynamicSize(10),
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#F0F0F2",
  },
  alertModalCancelText: {
    fontFamily: "Sen-Bold",
    color: "#666",
  },
  alertModalOkButton: {
    flex: 1,
    height: dynamicSize(44),
    borderRadius: dynamicSize(10),
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: Colours.PRIMARY,
  },
  alertModalOkText: {
    fontFamily: "Sen-Bold",
    color: "white",
  },
});

// import React, { useState, useMemo, useContext } from "react";
// import {
//   SafeAreaView,
//   View,
//   Text,
//   TextInput,
//   TouchableOpacity,
//   KeyboardAvoidingView,
//   ScrollView,
//   Platform,
//   ActivityIndicator,
//   Modal,
//   StyleSheet,
//   PixelRatio,
//   Dimensions,
// } from "react-native";
// import { MaterialCommunityIcons } from "@expo/vector-icons";
// import { useNavigation, useRoute } from "@react-navigation/native";
// import Colours from "../../utils/Colours";
// import AppContext from "../../context/AppContext";
// import useAxiosPrivate from "../../hooks/useAxiosPrivate";
// import axios from "../../api/axios";
// import TopHeaderComponent from "../aTopHeader/TopHeaderComponent";
// import ErrorAlertComp from "../../alertComp/ErrorAlertComp";

// const { width, height } = Dimensions.get("window");
// const BASE_WIDTH = 375;
// const BASE_HEIGHT = 812;

// const dynamicFontSize = (size) => {
//   const scaleFactor = Math.min(width / BASE_WIDTH, height / BASE_HEIGHT);
//   return Math.round(PixelRatio.roundToNearestPixel(size * scaleFactor));
// };
// const dynamicSpacing = (size) => (width / BASE_WIDTH) * size;
// const dynamicSize = (size) => (width / BASE_WIDTH) * size;

// const ReviewAndPay = () => {
//   const [isErrorCompVisible, setIsErrorCompVisible] = useState(false);
//   const [errorMessage, setErrorMessage] = useState("");
//   const [isLoading, setIsLoading] = useState(false);

//   // Selection Logic: Default to paying total order bill
//   const [payTotalBill, setPayTotalBill] = useState(true);
//   const [whatsappNumber, setWhatsappNumber] = useState("");
//   const [customAmount, setCustomAmount] = useState("");
//   const [isCustomSelected, setIsCustomSelected] = useState(false);
//   const [isItemsExpanded, setIsItemsExpanded] = useState(false);

//   // Paystack Native Confirmation Alert States
//   const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);

//   const navigation = useNavigation();
//   const route = useRoute();
//   const { userToken } = useContext(AppContext);
//   const axiosPrivate = useAxiosPrivate();
//   const isLoggedIn = !!userToken;

//   // Retrieve incoming transaction parameters passed seamlessly via route context
//   const selectedTable = route?.params?.selectedTable;
//   const tableId = selectedTable?._id;
//   const tableName = selectedTable?.tableName || "Table Summary";

//   // Directly pull current transaction cost or fallback safely to zero structure
//   const orderTotalBill = selectedTable?.estimatedTotalBill || selectedTable?.totalSpent || 0;

//   // Automatically extracts and flattens all items from all order batches
//   const flattenedItemsList = useMemo(() => {
//     if (!selectedTable?.orders || !Array.isArray(selectedTable.orders)) return [];

//     return selectedTable.orders.flatMap((orderBatch) =>
//       orderBatch.items.map((item) => ({
//         // Fallback to mongo _id or generate a unique composite key
//         id: item._id || `${orderBatch.orderToken}-${item.menuItemId}`,
//         name: item.name,
//         qty: item.quantity,
//         totalPrice: item.totalItemPrice,
//       })),
//     );
//   }, [selectedTable?.orders]);

//   // Dynamically extract target payload sum depending on layout selection state
//   const finalCalculatedSum = useMemo(() => {
//     if (payTotalBill) return orderTotalBill;
//     return Number(customAmount.replace(/[^0-9]/g, "")) || 0;
//   }, [payTotalBill, orderTotalBill, customAmount]);

//   const canProceed = useMemo(() => {
//     const isAmountValid = finalCalculatedSum > 0;
//     return isLoggedIn ? isAmountValid : isAmountValid && whatsappNumber?.length > 10;
//   }, [finalCalculatedSum, isLoggedIn, whatsappNumber]);

//   // Intercept checkout submit chain to show native confirmation notification popup
//   const handleProceedClick = () => {
//     if (!canProceed) return;
//     setIsConfirmModalVisible(true);
//   };

//   // Triggers once user gives confirmation on the modal overlay screen layout
//   const executePaymentGatewayPayload = async () => {
//     setIsConfirmModalVisible(false);
//     let finalPhoneNumber;

//     if (!isLoggedIn) {
//       let cleaned = whatsappNumber.replace(/\D/g, "").replace(/^0+/, "");
//       if (cleaned.startsWith("234")) cleaned = cleaned.slice(3);
//       cleaned = cleaned.replace(/^0+/, "");

//       if (cleaned.length !== 10) {
//         setErrorMessage("You might have entered a wrong number");
//         setIsErrorCompVisible(true);
//         return;
//       }
//       finalPhoneNumber = `234${cleaned}`;
//     }

//     const payload = {
//       phoneNumber: finalPhoneNumber,
//       customSpend: finalCalculatedSum,
//       associatedBrand: selectedTable?.associatedBrand,
//       waiterID: selectedTable?.waiterID,
//       tableId: tableId,
//     };

//     try {
//       setIsLoading(true);
//       if (isLoggedIn) {
//         const response = await axiosPrivate.post("/createVirtualTable/createVirtualTable", payload);
//         if (response.data.success) {
//           navigation.navigate("payForTable", { tableId: response.data.tableData._id });
//         }
//       } else {
//         const response = await axios.post("/requestOtp/requestOtp", payload);
//         if (response.status === 200) {
//           navigation.navigate("anonymousOnboardingOTP", { whatsappNumber: payload.phoneNumber });
//         }
//       }
//     } catch (error) {
//       console.error("Something went wrong processing your transaction context", error);
//       setErrorMessage("Something went wrong. Please try again");
//       setIsErrorCompVisible(true);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   return (
//     <SafeAreaView style={styles.safeArea}>
//       <TopHeaderComponent headerText={tableName} hideScreen={() => navigation.goBack()} />

//       <KeyboardAvoidingView
//         behavior={Platform.OS === "ios" ? "padding" : "height"}
//         style={{ flex: 1 }}
//       >
//         <ScrollView
//           contentContainerStyle={{
//             padding: dynamicSpacing(20),
//             paddingBottom: dynamicSpacing(200),
//             backgroundColor: Colours.LIGHTEST,
//           }}
//           showsVerticalScrollIndicator={false}
//         >
//           {/* ── SECTION 1: DEFAULT TOTAL BILL FROM ORDERS ── */}
//           <View style={styles.section}>
//             <Text style={styles.sectionLabel}>TOTAL BILL FROM ORDERS</Text>
//             <TouchableOpacity
//               style={[styles.billCard, payTotalBill && styles.billCardActive]}
//               onPress={() => {
//                 setPayTotalBill(true);
//                 setIsCustomSelected(false);
//                 setCustomAmount("");
//               }}
//               activeOpacity={0.85}
//             >
//               <View style={styles.billCardHeader}>
//                 <MaterialCommunityIcons
//                   name="receipt"
//                   size={dynamicSize(22)}
//                   color={payTotalBill ? "white" : Colours.PRIMARY}
//                 />
//                 <Text style={[styles.billCardTitle, payTotalBill && { color: "white" }]}>
//                   Order Digital Total
//                 </Text>
//                 {payTotalBill && (
//                   <MaterialCommunityIcons
//                     name="check-circle"
//                     size={dynamicSize(20)}
//                     color="white"
//                     style={{ marginLeft: "auto" }}
//                   />
//                 )}
//               </View>
//               <Text style={[styles.billCardAmount, payTotalBill && { color: "white" }]}>
//                 ₦{orderTotalBill.toLocaleString()}
//               </Text>

//               {/* Expand/Collapse Orders Tray */}
//               <TouchableOpacity
//                 onPress={() => setIsItemsExpanded(!isItemsExpanded)}
//                 style={[styles.expandToggleRow, payTotalBill && styles.expandToggleRowActive]}
//                 activeOpacity={0.7}
//               >
//                 <Text style={[styles.expandToggleText, payTotalBill && { color: "white" }]}>
//                   {isItemsExpanded ? "Collapse itemized view" : "Expand to see all items"}
//                 </Text>
//                 <MaterialCommunityIcons
//                   name={isItemsExpanded ? "chevron-up" : "chevron-down"}
//                   size={16}
//                   color={payTotalBill ? "white" : "#666"}
//                 />
//               </TouchableOpacity>

//               {isItemsExpanded && (
//                 <View style={[styles.itemsContainer, payTotalBill && styles.itemsContainerActive]}>
//                   {flattenedItemsList.map((item) => (
//                     <View key={item.id} style={styles.itemRow}>
//                       <Text style={[styles.itemNameText, payTotalBill && { color: "white" }]}>
//                         {item.qty}x {item.name}
//                       </Text>
//                       <Text style={[styles.itemPriceText, payTotalBill && { color: "white" }]}>
//                         ₦{item.totalPrice.toLocaleString()}
//                       </Text>
//                     </View>
//                   ))}
//                   {flattenedItemsList.length === 0 && (
//                     <Text
//                       style={[
//                         styles.expandToggleText,
//                         payTotalBill && { color: "white" },
//                         { textAlign: "center" },
//                       ]}
//                     >
//                       No items found in this order.
//                     </Text>
//                   )}
//                 </View>
//               )}
//             </TouchableOpacity>
//           </View>

//           {/* ── SECTION 2: ALTERNATIVE CUSTOM WAITER BILL ENTRY ── */}
//           {/* <View style={styles.section}>
//             <Text style={styles.sectionLabel}>ENTER AMOUNT TOLD BY WAITER</Text>
//             <View style={styles.inputSection}>
//               <View style={[styles.premiumInputContainer, isCustomSelected && styles.inputFocused]}>
//                 <Text style={styles.inputCurrency}>₦</Text>
//                 <TextInput
//                   style={styles.fieldInput}
//                   placeholder="Input custom waiter stated amount"
//                   placeholderTextColor="#A3A3A3"
//                   keyboardType="numeric"
//                   value={customAmount}
//                   onChangeText={(text) => {
//                     const cleaned = text.replace(/[^0-9]/g, "");
//                     setCustomAmount(cleaned);
//                     setIsCustomSelected(cleaned !== "");

//                     // Automatically switch active selection state once form input begins
//                     if (cleaned !== "") {
//                       setPayTotalBill(false);
//                     } else {
//                       setPayTotalBill(true);
//                     }
//                   }}
//                   onFocus={() => {
//                     setIsCustomSelected(true);
//                     setPayTotalBill(false);
//                   }}
//                 />
//               </View>
//             </View>
//           </View> */}

//           {/* ── SECTION 3: ANONYMOUS GUEST PHONE REGISTRATION ── */}
//           {/* {!isLoggedIn && (
//             <View style={styles.section}>
//               <Text style={styles.sectionLabel}>WHATSAPP PHONE NUMBER</Text>
//               <View style={styles.inputSection}>
//                 <View style={styles.premiumInputContainer}>
//                   <TextInput
//                     style={styles.fieldInput}
//                     placeholder="e.g. 08031234567"
//                     placeholderTextColor="#A3A3A3"
//                     keyboardType="phone-pad"
//                     value={whatsappNumber}
//                     onChangeText={setWhatsappNumber}
//                   />
//                 </View>
//               </View>
//             </View>
//           )} */}

//           {/* ── FINAL SUBMISSION BUTTON TRIGGER ── */}
//           {/* <TouchableOpacity
//             style={[
//               styles.proceedBtn,
//               !canProceed && styles.disabledBtn,
//               { marginTop: dynamicSpacing(24) },
//             ]}
//             onPress={handleProceedClick}
//             disabled={!canProceed || isLoading}
//           >
//             {isLoading ? (
//               <ActivityIndicator color="white" />
//             ) : (
//               <>
//                 <Text style={styles.proceedBtnText}>
//                   {isLoggedIn ? "Confirm & Pay Stated Sum" : "Verify Number via OTP"}
//                 </Text>
//                 <MaterialCommunityIcons
//                   name={isLoggedIn ? "credit-card-outline" : "arrow-right"}
//                   size={dynamicSize(20)}
//                   color="white"
//                 />
//               </>
//             )}
//           </TouchableOpacity>

//           <Text style={styles.footerNote}>
//             Paystack instant transaction systems secure and tokenise checkout verification layers.
//           </Text> */}
//         </ScrollView>
//       </KeyboardAvoidingView>

//       {/* ── PAYSTACK SYSTEM LEVEL INTEGRATION NOTICE ALERT OVERLAY ── */}
//       {/* <Modal visible={isConfirmModalVisible} transparent={true} animationType="fade">
//         <View style={styles.alertModalCenteredBackdrop}>
//           <View style={styles.alertModalContainerBox}>
//             <View style={styles.alertIconHeaderContainer}>
//               <MaterialCommunityIcons name="shield-check" size={32} color={Colours.PRIMARY} />
//             </View>
//             <Text style={styles.alertModalHeading}>Confirm Bill Total</Text>
//             <Text style={styles.alertModalMessage}>
//               You are about to pay a total sum of {"\n"}
//               <Text style={styles.alertModalAmountHighlight}>
//                 ₦{finalCalculatedSum.toLocaleString()}
//               </Text>
//             </Text>

//             <View style={styles.alertModalButtonWrapper}>
//               <TouchableOpacity
//                 style={styles.alertModalCancelButton}
//                 onPress={() => setIsConfirmModalVisible(false)}
//               >
//                 <Text style={styles.alertModalCancelText}>Cancel</Text>
//               </TouchableOpacity>

//               <TouchableOpacity
//                 style={styles.alertModalOkButton}
//                 onPress={executePaymentGatewayPayload}
//               >
//                 <Text style={styles.alertModalOkText}>Okay</Text>
//               </TouchableOpacity>
//             </View>
//           </View>
//         </View>
//       </Modal>

//       <Modal visible={isErrorCompVisible} transparent={true} animationType="fade">
//         <ErrorAlertComp message={errorMessage} onClose={() => setIsErrorCompVisible(false)} />
//       </Modal> */}
//     </SafeAreaView>
//   );
// };

// export default ReviewAndPay;

// const styles = StyleSheet.create({
//   safeArea: { flex: 1, backgroundColor: "white" },
//   section: { marginBottom: dynamicSpacing(25) },
//   sectionLabel: {
//     fontFamily: "Sen-Bold",
//     fontSize: dynamicFontSize(11),
//     color: "#888",
//     letterSpacing: 1.2,
//     marginBottom: dynamicSpacing(12),
//   },
//   billCard: {
//     backgroundColor: "white",
//     borderRadius: dynamicSize(16),
//     padding: dynamicSpacing(18),
//     borderWidth: 1,
//     borderColor: "#EEE",
//     elevation: 2,
//     shadowColor: "#000",
//     shadowOffset: { width: 0, height: 1 },
//     shadowOpacity: 0.06,
//     shadowRadius: 4,
//   },
//   billCardActive: {
//     backgroundColor: Colours.PRIMARY,
//     borderColor: Colours.PRIMARY,
//   },
//   billCardHeader: {
//     flexDirection: "row",
//     alignItems: "center",
//     marginBottom: dynamicSpacing(8),
//     gap: dynamicSpacing(8),
//   },
//   billCardTitle: {
//     fontFamily: "Sen-Bold",
//     fontSize: dynamicFontSize(16),
//     color: "#1A1A1A",
//   },
//   billCardAmount: {
//     fontFamily: "Sen-Bold",
//     fontSize: dynamicFontSize(28),
//     color: Colours.PRIMARY,
//     marginBottom: dynamicSpacing(8),
//   },
//   expandToggleRow: {
//     flexDirection: "row",
//     alignItems: "center",
//     gap: 4,
//     paddingVertical: 6,
//     borderTopWidth: 1,
//     borderColor: "#F0F0F0",
//     marginTop: 4,
//   },
//   expandToggleRowActive: {
//     borderColor: "rgba(255,255,255,0.15)",
//   },
//   expandToggleText: {
//     fontSize: dynamicFontSize(12),
//     fontFamily: "Sen-Regular",
//     color: "#666",
//   },
//   itemsContainer: {
//     marginTop: 8,
//     paddingTop: 8,
//     gap: 8,
//   },
//   itemRow: {
//     flexDirection: "row",
//     justifyContent: "space-between",
//     alignItems: "center",
//   },
//   itemNameText: {
//     fontSize: dynamicFontSize(13),
//     color: "#444",
//     fontFamily: "Sen-Regular",
//   },
//   itemPriceText: {
//     fontSize: dynamicFontSize(13),
//     color: "#1A1A1A",
//     fontFamily: "Sen-Bold",
//   },
//   inputSection: { paddingHorizontal: dynamicSpacing(2) },
//   premiumInputContainer: {
//     flexDirection: "row",
//     alignItems: "center",
//     backgroundColor: "#FAFAFA",
//     borderWidth: dynamicSize(1.5),
//     borderColor: "#EAEAEA",
//     borderRadius: dynamicSize(12),
//     paddingHorizontal: dynamicSpacing(16),
//     height: dynamicSize(56),
//   },
//   inputFocused: { borderColor: "#1A1A1A", backgroundColor: "#FFFFFF" },
//   inputCurrency: {
//     fontSize: dynamicFontSize(18),
//     fontWeight: "600",
//     color: "#1A1A1A",
//     marginRight: dynamicSpacing(6),
//   },
//   fieldInput: {
//     flex: 1,
//     fontSize: dynamicFontSize(16),
//     fontWeight: "600",
//     color: "#1A1A1A",
//     padding: 0,
//   },
//   proceedBtn: {
//     backgroundColor: Colours.PRIMARY,
//     flexDirection: "row",
//     justifyContent: "center",
//     alignItems: "center",
//     paddingVertical: dynamicSpacing(18),
//     borderRadius: dynamicSize(15),
//     gap: dynamicSpacing(10),
//   },
//   disabledBtn: { backgroundColor: "#CCC" },
//   proceedBtnText: {
//     color: "white",
//     fontFamily: "Sen-Bold",
//     fontSize: dynamicFontSize(16),
//   },
//   footerNote: {
//     textAlign: "center",
//     color: "#AAA",
//     fontSize: dynamicFontSize(11),
//     marginTop: dynamicSpacing(20),
//   },
//   /* ── CUSTOM PAYSTACK NOTICE POPUP STYLES ── */
//   alertModalCenteredBackdrop: {
//     flex: 1,
//     backgroundColor: "rgba(0, 0, 0, 0.4)",
//     justifyContent: "center",
//     alignItems: "center",
//     padding: 24,
//   },
//   alertModalContainerBox: {
//     width: "100%",
//     maxWidth: 320,
//     backgroundColor: "white",
//     borderRadius: 20,
//     padding: 24,
//     alignItems: "center",
//     shadowColor: "#000",
//     shadowOffset: { width: 0, height: 4 },
//     shadowOpacity: 0.15,
//     shadowRadius: 12,
//     elevation: 8,
//   },
//   alertIconHeaderContainer: {
//     width: 60,
//     height: 60,
//     borderRadius: 30,
//     backgroundColor: "#FFF4F2",
//     justifyContent: "center",
//     alignItems: "center",
//     marginBottom: 16,
//   },
//   alertModalHeading: {
//     fontFamily: "Sen-Bold",
//     fontSize: 18,
//     color: "#1A1A1A",
//     marginBottom: 8,
//     textAlign: "center",
//   },
//   alertModalMessage: {
//     fontFamily: "Sen-Regular",
//     fontSize: 14,
//     color: "#555",
//     textAlign: "center",
//     lineHeight: 22,
//     marginBottom: 24,
//   },
//   alertModalAmountHighlight: {
//     fontFamily: "Sen-Bold",
//     fontSize: 20,
//     color: Colours.PRIMARY,
//   },
//   alertModalButtonWrapper: {
//     flexDirection: "row",
//     width: "100%",
//     gap: 12,
//   },
//   alertModalCancelButton: {
//     flex: 1,
//     paddingVertical: 12,
//     borderRadius: 10,
//     backgroundColor: "#F5F5F5",
//     alignItems: "center",
//     justifyContent: "center",
//   },
//   alertModalCancelText: {
//     fontFamily: "Sen-Bold",
//     fontSize: 14,
//     color: "#666",
//   },
//   alertModalOkButton: {
//     flex: 1,
//     paddingVertical: 12,
//     borderRadius: 10,
//     backgroundColor: Colours.PRIMARY,
//     alignItems: "center",
//     justifyContent: "center",
//   },
//   alertModalOkText: {
//     fontFamily: "Sen-Bold",
//     fontSize: 14,
//     color: "white",
//   },
// });
